import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, LogIn, Mail } from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { useCommon } from "../context/CommonContext";
import { useToast } from "../hooks/use-toast";
import { useAuthOverlay } from "./AuthOverlay";
import AuthShell from "./AuthShell";
import { BASE_URL } from "../lib/utils";
import { getAuthFlags } from "./authHelpers";

import { Button } from "../components/ui/button";
import AuthFormField from "../components/layout/AuthFormField";
import TurnstilWidget from "../components/layout/TurnstilWidget";

export const LoginPage = () => {
	const { open,close } = useAuthOverlay();
	const { settings } = useCommon();

	const { login, loginWithGoogleToken, isLoading, error } = useAuth();
	const { toast } = useToast();

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [captchaVerified, setCaptchaVerified] = useState(false);
	const [rememberMe, setRememberMe] = useState(false);
	const [showEmailForm, setShowEmailForm] = useState(false);

	const {
		allowEmailPassword,
		allowGitLogin,
		allowGoogleLogin,
		allowLinkedInLogin,
	} = getAuthFlags(settings);

	const allowGit = !!allowGitLogin;
	const allowGoogle = !!allowGoogleLogin;
	const allowLinkedIn = !!allowLinkedInLogin;
	const allDisabled = !allowEmailPassword && !allowGit && !allowGoogle && !allowLinkedIn;

	useEffect(() => {
		const stored = localStorage.getItem("remember-me") === "true";
		setRememberMe(stored);
	}, []);

	const handleChangeRememberMe = useCallback((event) => {
		setRememberMe(event.target.checked);
		localStorage.setItem("remember-me", event.target.checked);
	}, []);

	const handleSubmit = useCallback(
		(event) => {
			event.preventDefault();

			if (!allowEmailPassword) return;

			if (!email || !password) {
				toast({
					title: "Missing credentials",
					description: "Please enter both email and password.",
					variant: "destructive",
				});
				return;
			}

			if (!captchaVerified) {
				toast({
					title: "CAPTCHA required",
					description: "Please verify CAPTCHA.",
					variant: "destructive",
				});
				return;
			}

			login({ email, password });
		},
		[allowEmailPassword, captchaVerified, email, login, password, toast]
	);

	const submitButtonDisabled =
		isLoading || (allowEmailPassword && (!email || !password || !captchaVerified));

	return (
		<AuthShell
			title="Welcome Back"
			description={
				<>
					Log in to continue to
					<span className="ml-1 font-bold text-foreground">CoffeeGamesOnline</span>
				</>
			}
			footerPrompt="Don&apos;t have an account?"
			footerActionLabel="Sign up"
			onFooterAction={() => open("signup")}
			toggleDescription="Continue using email"
			toggleIcon={<Mail className="h-5 w-5 text-primary" />}
			showEmailForm={showEmailForm}
			onToggleEmailForm={setShowEmailForm}
			allowEmailPassword={allowEmailPassword}
			allDisabled={allDisabled}
			error={error}
			emailDisabledMessage="Email/password login is disabled."
			allowGit={allowGit}
			allowGoogle={allowGoogle}
			allowLinkedIn={allowLinkedIn}
			onGithub={() => allowGit && (window.location.href = `${BASE_URL}/auth/github`)}
			onGoogle={(token) => allowGoogle && loginWithGoogleToken?.(token)}
			isLoading={isLoading}
			preFormContent={
				null
			}
		>
			<motion.form
				initial={{ opacity: 0, height: 0 }}
				animate={{ opacity: 1, height: "auto" }}
				onSubmit={handleSubmit}
				className="space-y-5"
			>
				<AuthFormField
					label="Email"
					type="email"
					value={email}
					onChange={(event) => setEmail(event.target.value)}
					placeholder="Enter your email"
					leftIcon={<Mail className="h-4 w-4" />}
				/>

				<AuthFormField
					label="Password"
					type="password"
					value={password}
					onChange={(event) => setPassword(event.target.value)}
					placeholder="Enter your password"
					leftIcon={<Lock className="h-4 w-4" />}
				/>

				<div className="flex items-center justify-between">
					<label className="flex items-center gap-2 text-sm text-muted-foreground">
						<input
							type="checkbox"
							checked={rememberMe}
							onChange={handleChangeRememberMe}
							className="h-4 w-4 rounded border-border"
						/>
						Remember me
					</label>

					<a href="/forgot-password" className="text-sm font-medium text-primary hover:opacity-80">
						Forgot password?
					</a>
				</div>

				<div className="overflow-hidden rounded-2xl border border-border/50 bg-background/50 p-3">
					<TurnstilWidget onVerify={setCaptchaVerified} />
				</div>

				<Button
					type="submit"
					className="h-12 w-full rounded-2xl text-base font-semibold shadow-lg transition-all duration-200 hover:scale-[1.01] active:scale-[0.98]"
					disabled={submitButtonDisabled}
					loading={isLoading}
					leftIcon={<LogIn className="h-5 w-5" />}
				>
					{isLoading ? "Logging in..." : "Log In"}
				</Button>
			</motion.form>
		</AuthShell>
	);
};